import React, { useEffect, useMemo, useState } from 'react';
import { useAPI } from '@arema/components/Hooks';
import { Groupper, Header, Icon, Label, Pagination, Table, Toolbar } from 'react-frontier2';
import { useTranslation } from 'react-i18next';
import { EventSectionProps } from '../../AdminClasses';
import { fromUnix } from '@arema/components/Util';
import { Money } from '@arema/components';
import { EventOrderConfig } from '@arema/types/internal';
import { PaymentMethods } from '@arema/components/Classes';
import API from '../../API';
import { Link } from 'react-router-dom';

const PAGE_SIZE = 50;
var EventOrders = (props: EventSectionProps)=>{
	var { t } = useTranslation();
	var { load, data, loadError, loading } = useAPI(API.getEventOrders, {
		onData: res=>{
			if(res.error) return;
			if(!!res.data?.count){
				setCount(res.data.count);
			}
		}
	});
	var [count, setCount] = useState<(typeof data)['count']>(null);
	var [config, setConfig] = useState<EventOrderConfig>({});
	var [search, setSearch] = useState<string>('');
	var [page, setPage] = useState<number>(0);

	useEffect(()=>{
		setCount(null);
		load(props.event.event_id, {
			...config,
			total_count: true,
		}, page*PAGE_SIZE, PAGE_SIZE);
	}, [config]);

	var onPageChange = (p: number)=>{
		setPage(p);
		load(props.event.event_id, {
			...config,
			total_count: false
		}, p*PAGE_SIZE, PAGE_SIZE);
	}

	var changeConfig = <T extends keyof EventOrderConfig,>(key: T, val: EventOrderConfig[T])=>{
		return ()=>{
			if(config[key]==val) return;
			setConfig(c=>({ ...c, [key]: val }));
		}
	}

	var has_config = useMemo(()=>{
		if(!config) return false;
		return !!config.payment_method || (!!config.type && config.type!=='both') || !!config.deposit || (!!config.dates && config.dates.length>0) || !!config.payment_method_type;
	}, [config]);

	var getPaymentMethodLocale = (m: number)=>{
		switch(m){
			case PaymentMethods.FREE: return 'Cortesías';
			case PaymentMethods.PRINTED_TICKET: return 'Boleto duro';
			default: return `Método: ${m}`
		}
	}
	
	return <div>
		<Groupper title={t('common.orders')} style={{ maxWidth: 1200, margin: 'auto' }}>
			<Toolbar stretch={false}>
				<Toolbar.Dropdown text='Tipo' items={[
					{ text: 'Todos', onClick: changeConfig('type', 'both'), iconName: !config?.type || config?.type==='both' ? 'circle-check' : 'circle', iconSolid: (!config?.type || config?.type==='both') },
					{ text: 'PDV', onClick: changeConfig('type', 'pdv'), iconName: config.type==='pdv' ? 'circle-check' : 'circle', iconSolid: config.type==='pdv' },
					{ text: 'Web', onClick: changeConfig('type', 'online'), iconName: config.type==='online' ? 'circle-check' : 'circle', iconSolid: config.type==='online' },
				]} />
				<Toolbar.Dropdown text='Método' items={[
					{ text: 'Todos', onClick: changeConfig('payment_method', null), iconName: !config.payment_method ? 'circle-check' : 'circle', iconSolid: (!config.payment_method) },
					{ text: 'Boleto duro', onClick: changeConfig('payment_method', PaymentMethods.PRINTED_TICKET), iconName: config.payment_method===PaymentMethods.PRINTED_TICKET ? 'circle-check' : 'circle', iconSolid: config.payment_method===PaymentMethods.PRINTED_TICKET },
					{ text: 'Cortesía', onClick: changeConfig('payment_method', PaymentMethods.FREE), iconName: config.payment_method===PaymentMethods.FREE ? 'circle-check' : 'circle', iconSolid: config.payment_method===PaymentMethods.FREE },
				]} />
			</Toolbar>
			<Table fitted selectable style={{ marginBottom: -20, marginTop: -20 }}>
				<Table.Head collapsingIndexes={[0, 1, 3, 4, 5, 6, 7]}>
					{!!has_config && (
						<Table.Cell row header colSpan={100}>
							<div style={{ display: 'flex', flexDirection: 'row', gap: 10 }}>
								{!!config?.type && config.type!=='both' && (
									<Label size='small'>{config.type==='pdv' ? 'PDV' : 'Online'}</Label>
								)}
								{!!config?.payment_method && (
									<Label size='small'>{getPaymentMethodLocale(config.payment_method)}</Label>
								)}
							</div>
						</Table.Cell>
					)}
					<Table.Row header headerCell data={['ID', 'Tipo', 'Correo', 'Fecha', 'Método', 'Estatus', 'Boletos', 'Total']} />
				</Table.Head>
				<Table.Body>
					{loadError ? (
						<Table.Cell centered row colSpan={100}>
							<Header text={t('common.error')} subtext={loadError} />
						</Table.Cell>
					) : !data ? (
						<Table.Cell centered row colSpan={100}>
							<Header text={t('common.loading')} size={'small'} loaderSize={50} loading />
						</Table.Cell>
					) : data.orders.map(a=>(
						<Table.Row compact
							collapsingIndexes={[0, 1, 3, 4, 5, 6, 7]}
							centeredIndexes={[1, 6]}
							as={Link}
							to={`/orders/${a.order_hash}`}
							target={'_blank'}
							data={[
								a.order_hash,
								<Icon name={a.pdv_id ? 'cash-register' : 'globe'} style={{ marginRight: 0 }} />,
								(a.email || <Icon name='minus' color='gray' />),
								(!!a.date_paid || !!a.date_created) ? fromUnix(a.date_paid || a.date_created).toFormat('dd/MM/yy HH:mm') : <Icon name='minus' color='gray' />,
								a.payment_methods.map(a=>a.method_name_internal).join(', '),
								<Label size='small' style={{ minWidth: 90 }} color={a.tickets_cancelled>=a.tickets ? 'red' : (a.tickets_paid>0 ? 'green' : 'blue')}>
									{t(`common.${a.tickets_cancelled>=a.tickets ? 'cancelled' : (a.tickets_paid>0 ? 'paid' : 'pending')}`)}
								</Label>,
								a.tickets,
								<Money value={a.total} />
							]}
						/>
					))}
				</Table.Body>
				{!!count && (
					<Table.Footer>
						<Table.Cell row colSpan={100}>
							<Pagination onPageChange={onPageChange} disabled={loading} page={page} pageCount={Math.ceil(count.orders/PAGE_SIZE)}  />
						</Table.Cell>
					</Table.Footer>
				)}
			</Table>
		</Groupper>
	</div>
}

export default EventOrders;